
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { wishlistService } from '../services/wishlistService';
import { Product, User, WishlistItem } from '../types';
import { 
  Heart, 
  ShoppingBag, 
  Trash2, 
  ArrowRight, 
  Package, 
  Home, 
  Zap,
  Star,
  ChevronRight,
  ShoppingCart
} from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

interface CustomerWishlistProps {
  user: User | null;
  onNavigateHome: () => void;
  onAddToCart: (product: Product) => void;
  onViewProduct: (product: Product) => void;
}

const CustomerWishlist: React.FC<CustomerWishlistProps> = ({ user, onNavigateHome, onAddToCart, onViewProduct }) => {
  const [wishlistItems, setWishlistItems] = useState<Product[]>([]);
  const { format } = useCurrency();
  const userId = user?.id || 'guest-session';

  useEffect(() => {
    loadWishlist();
    window.addEventListener('wishlist-update', loadWishlist);
    return () => window.removeEventListener('wishlist-update', loadWishlist);
  }, [userId]);

  const loadWishlist = () => {
    const items = wishlistService.getWishlist(userId);
    const allProducts = storage.getProducts();
    const wishProducts = items.map(i => allProducts.find(p => p.id === i.productId)).filter(Boolean) as Product[];
    setWishlistItems(wishProducts);
  };

  const handleRemove = (product: Product) => {
    wishlistService.toggleWishlist(userId, product);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
             <Heart className="h-10 w-10 text-red-500 fill-current" /> My Wishlist
          </h1>
          <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px] mt-1">Saved Tech Desires</p>
        </div>
        <button onClick={onNavigateHome} className="flex items-center gap-2 text-sm font-black text-blue-600 bg-blue-50 px-6 py-3 rounded-2xl hover:bg-blue-100 transition-all">
          <Home className="h-4 w-4" /> Continue Exploring
        </button>
      </div>

      {wishlistItems.length === 0 ? (
        <div className="text-center py-32 bg-white rounded-[3rem] border border-dashed border-gray-200">
           <Heart className="h-16 w-16 text-gray-200 mx-auto mb-6" />
           <h3 className="text-xl font-bold text-gray-900">Your wishlist is empty, bhai.</h3>
           <p className="text-gray-400 mt-2 mb-8">Save products you love and track their availability.</p>
           <button onClick={onNavigateHome} className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl">
              Go to Marketplace
           </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
           {wishlistItems.map((product) => (
             <div key={product.id} className="bg-white rounded-[40px] border border-gray-50 overflow-hidden shadow-sm hover:shadow-2xl transition-all group flex flex-col">
                <div className="relative aspect-video overflow-hidden">
                   <img src={product.images[0]} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt="" />
                   <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors"></div>
                   <button 
                     onClick={() => handleRemove(product)}
                     className="absolute top-4 right-4 p-3 bg-white/90 backdrop-blur rounded-2xl text-red-500 shadow-lg hover:scale-110 transition-all"
                   >
                      <Trash2 className="h-4 w-4" />
                   </button>
                   {product.stock < 10 && product.stock > 0 && (
                     <div className="absolute bottom-4 left-4">
                        <span className="bg-orange-600 text-white text-[8px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-lg flex items-center gap-1">
                           <Zap className="h-2 w-2 fill-current" /> Only {product.stock} left!
                        </span>
                     </div>
                   )}
                   {product.stock === 0 && (
                     <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center">
                        <span className="bg-gray-900 text-white text-[10px] font-black px-6 py-3 rounded-2xl uppercase tracking-widest">Out of Stock</span>
                     </div>
                   )}
                </div>
                <div className="p-8 flex-grow space-y-4">
                   <div className="flex justify-between items-start gap-4">
                      <div>
                        <h3 onClick={() => onViewProduct(product)} className="font-black text-xl text-gray-900 leading-tight cursor-pointer hover:text-blue-600 transition-colors line-clamp-1">{product.name}</h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Ref ID: {product.id}</p>
                      </div>
                      <span className="font-black text-2xl text-gray-900">{format(product.price)}</span>
                   </div>
                   <div className="flex gap-3 pt-4 border-t border-gray-50">
                      <button 
                        onClick={() => onViewProduct(product)}
                        className="flex-1 bg-gray-50 text-gray-600 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-100 transition-all"
                      >
                         View Details
                      </button>
                      <button 
                        onClick={() => onAddToCart(product)}
                        disabled={product.stock === 0}
                        className="flex-1 bg-blue-600 text-white py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all disabled:opacity-30 disabled:shadow-none flex items-center justify-center gap-2"
                      >
                         <ShoppingCart className="h-4 w-4" /> Buy Now
                      </button>
                   </div>
                </div>
             </div>
           ))}
        </div>
      )}
    </div>
  );
};

export default CustomerWishlist;
